import React, { useEffect } from 'react';
import { styled } from '@compiled/react';
import { isHelpCenterMainContainerEnabled } from '@helpcenter/feature-flags';
import { FlexibleHomepageLoader } from '@helpcenter/flexible-homepage-loader';
import { useInView } from 'react-intersection-observer';
import { di } from 'react-magnetic-di';
import { usePaginationFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { LayoutHeader } from 'view/layout-next/layout-header';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box, Stack } from '@atlaskit/primitives';
import { TRACK_EVENT_TYPE } from '@atlassian/analytics-web-react';
import type { FetchError } from '@atlassian/help-center-common-util/fetch';
import { isFetchNetworkOrClientError } from '@atlassian/help-center-common-util/fetch';
import { SectionViewRenderer } from '../../layout-primitives/section-view-renderer';
import { LAYOUT_VIEW_RENDERER_PAGE_SIZE } from '../constants';
import type { layoutViewRendererFragment$key } from './__generated__/layoutViewRendererFragment.graphql';
interface LayoutRendererProps {
    layout: layoutViewRendererFragment$key;
}

const LayoutViewRenderer: React.FC<LayoutRendererProps> = (props) => {
    di(SectionViewRenderer);
    di(useInView);
    di(LayoutHeader);
    di(usePaginationFragment);
    di(FlexibleHomepageLoader);
    di(useAnalyticsEvents);
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
        graphql`
            fragment layoutViewRendererFragment on HelpLayout
            @argumentDefinitions(count: { type: "Int", defaultValue: 10 }, cursor: { type: "String" })
            @refetchable(queryName: "layoutRendererRefetchQuery") {
                __typename
                sections(first: $count, after: $cursor) @connection(key: "layoutRendererFragment_sections") {
                    __typename
                    edges {
                        node {
                            __id
                            # It does not recognize this valid fragment spread since we are using a re-export from the request forms package's entry point.
                            # It does not report an error if we consume this from the module - section, directly.
                            # eslint-disable-next-line relay/must-colocate-fragment-spreads
                            ...sectionViewRendererFragment
                            ...layoutHeaderFragment
                        }
                    }
                }
            }
        `,
        props.layout
    );
    const firstSectionRef = data.sections?.edges?.[0]?.node;
    const sections = data?.sections?.edges?.map((edge) => edge?.node);
    const { ref, inView } = useInView({
        threshold: 0,
    });
    useEffect(() => {
        if (hasNext && inView) {
            loadNext(LAYOUT_VIEW_RENDERER_PAGE_SIZE, {
                onComplete: (error: Error | FetchError | null) => {
                    if (error) {
                        if (!isFetchNetworkOrClientError(error as FetchError)) {
                            createAnalyticsEvent({
                                action: 'pagination_failed',
                                actionSubject: 'homepageLayout',
                                analyticsType: TRACK_EVENT_TYPE,
                                error: error.message,
                            }).fire();
                        }
                    } else {
                        createAnalyticsEvent({
                            action: 'pagination_success',
                            actionSubject: 'homepageLayout',
                            analyticsType: TRACK_EVENT_TYPE,
                        }).fire();
                    }
                },
            });
        }
    }, [createAnalyticsEvent, hasNext, inView, loadNext]);

    if (!sections) {
        return null;
    }

    return (
        <LayoutViewRendererWrapper data-testid="layout-view-renderer">
            {firstSectionRef ? <LayoutHeader firstSectionFragment={firstSectionRef} /> : null}
            <Box as={isHelpCenterMainContainerEnabled() ? 'main' : 'div'}>
                <Stack>
                    {sections.map((section) => {
                        if (!section) {
                            return null;
                        }
                        return <SectionViewRenderer section={section} key={section.__id} />;
                    })}
                </Stack>

                {/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop, @atlaskit/ui-styling-standard/enforce-style-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve */}
                <div className="pagination-sentinel" style={{ height: '20px' }} ref={ref}></div>
                {isLoadingNext && <FlexibleHomepageLoader removeHeader sectionCount={2} />}
            </Box>
        </LayoutViewRendererWrapper>
    );
};

export default LayoutViewRenderer;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const LayoutViewRendererWrapper = styled.div({
    position: 'relative',
});
